/**
 * @module app.shared
 */

import { autoinject, bindable, observable } from 'aurelia-framework';
import { fireEventOn } from 'app/shared/lib/fire-events';
import { addDelegatedEventListener, removeDelegatedEventListener } from 'app/shared/lib/listen-events';
import { EssFocusableCustomAttribute } from 'app/shared/attributes/ess-focusable-attribute';

type Direction = null | 'asc' | 'desc';
type Sorting = Readonly<{ property: string, direction: Direction }>;
export type SortChangeEvent = CustomEvent<Sorting>;

@autoinject()
export class SortableCustomAttribute extends EssFocusableCustomAttribute {

    @bindable({ primaryProperty: true }) property: string | false = false;
    @bindable() target: string | null = null;
    @observable() dir: Direction = null;

    protected handleClick = () => {
        if(this.property === false) {
            return;
        }
        this.toggleSort();
    };

    protected attached() {
        if(this.property === false) {
            return;
        }
        super.attached();
        addDelegatedEventListener(this.element, 'click', this.handleClick);
    }

    protected detached() {
        super.detached();
        removeDelegatedEventListener(this.element, 'click', this.handleClick);
    }

    public sortAscending() {
        this.dir = 'asc';
    }

    public sortDescending() {
        this.dir = 'desc';
    }

    public toggleSort() {
        if(this.dir === null) {
            this.dir = 'asc';
        } else if(this.dir === 'asc') {
            this.dir = 'desc';
        } else if(this.dir === 'desc') {
            this.dir = null;
        }
    }

    protected updateClass() {
        this.element.classList.remove('sort-asc', 'sort-desc');
        this.element.parentElement?.removeAttribute('aria-sort');
        if(this.dir === 'asc') {
            this.element.classList.add('sort-asc');
            this.element.parentElement?.setAttribute('aria-sort', 'ascending');
        }
        else if(this.dir === 'desc') {
            this.element.classList.add('sort-desc');
            this.element.parentElement?.setAttribute('aria-sort', 'descending');
        } else {
            this.element.parentElement?.setAttribute('aria-sort', 'none');
        }
    }

    protected dirChanged() {
        this.updateClass();
        const event = 'sortchange';
        const detail = Object.freeze({ property: this.property, direction: this.dir });
        const target = this.target ? this.element.closest(this.target) : this.element;

        // TODO [Matt] Perhaps log a warning if a target is not found.
        if(target) {
            fireEventOn(target, event, detail);
        }
    }
}
